import React, {useEffect, useRef, useState} from "react";
import {
    Box,
    Button,
    FormControl, Grid,
    Slider,
    TextField,
    Typography
} from "@mui/material";
import {DEFAULT_AUDIO_TRACK, FADE_MAX, FADE_MIN, FADE_STEP} from "../../../constants/materializationConstants";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import {useMaterializationSettings} from "../../../contexts/MaterializationSettingsContext";
import { ASPECT_RATIO_OPTIONS } from "../../../reactvideoeditor/pro/components/advanced-timeline/components/timeline-header/aspect-ratio-dropdown";
import Autocomplete from "@mui/material/Autocomplete";
import {getEventAudioTracks} from "@/queries/eventQueries";
import {enqueueSnackbar} from "notistack";
import {FilterOption} from "@/types/filterTypes";
import CustomAutoComplete from "@/pages/custom-components/CommonComponents/CustomAutoComplete";
import InputAdornment from "@mui/material/InputAdornment";
import {Check} from "lucide-react";
import { useEditorialAssets } from "../../../contexts/EditorialAssetsContext";
import {AGGREGATION_USAGE_TYPE} from "@/pages/extra-pages/ContentAggregation";
import {useIntl} from "react-intl";


interface MaterializationSettingsProps {
    usageMode: AGGREGATION_USAGE_TYPE;
    audioTrackAutoSelection?: boolean;
    eventIds: string[];
    showAudioTrackSelection?: boolean;
    showLCut?: boolean;
    disableLCut?: boolean;
    onAudioTrackOptionLoad?: (reason: 'error' | 'warning' | 'success', info: any) => void;
    onAudioTrackLoading?: (loading: boolean) => void;
}

const MaterializationSettings: React.FC<MaterializationSettingsProps> = ({usageMode, eventIds, audioTrackAutoSelection = true, onAudioTrackOptionLoad = () => {}, showAudioTrackSelection = true, showLCut = false, disableLCut = false, onAudioTrackLoading = () => {} }) => {

    const {fade, setFade, fadeDuration, setFadeDuration, lCut, setLCut, aspectRatio, setAspectRatio, audioTrack, setAudioTrack} = useMaterializationSettings()
    const [audioTrackOptions, setAudioTrackOptions] = useState<FilterOption[]>([])
    const {resetEditorialAssetSelection} = useEditorialAssets();

    const trackCacheRef = useRef<Record<string, FilterOption[]>>({});
    const inFlightRequestsRef = useRef<Record<string, Promise<FilterOption[]>>>({});

    const intl = useIntl();

    const loadAutoTracks = async () => {
        try {
            if (usageMode === AGGREGATION_USAGE_TYPE.PRESET) return;

            onAudioTrackLoading(true);

            if (!eventIds || eventIds.length === 0) {
                setAudioTrackOptions([]);
                onAudioTrackOptionLoad("success", { availableTracksPerEvent: {} });
                onAudioTrackLoading(false);
                return;
            }

            // 1. Trova SOLO gli eventi che non sono ancora nella cache
            const eventsToFetch = eventIds.filter(id => !trackCacheRef.current[id]);

            // 2. Fai le chiamate solo per quelli mancanti
            if (eventsToFetch.length > 0) {
                const trackPromises = eventsToFetch.map(async (id) => {
                    // 1. Se c'è GIÀ una chiamata in corso per questo ID, ci agganciamo a quella e la aspettiamo!
                    if (inFlightRequestsRef.current[id]) {
                        const tracks = await inFlightRequestsRef.current[id];
                        return { eventId: id, tracks };
                    }

                    // 2. Altrimenti, avviamo la vera chiamata di rete...
                    const requestPromise = getEventAudioTracks(id);

                    // ... e la salviamo IMMEDIATAMENTE nella ref delle chiamate in corso
                    inFlightRequestsRef.current[id] = requestPromise;

                    try {
                        const tracks = await requestPromise;
                        return { eventId: id, tracks };
                    } catch (error) {
                        // Se la chiamata fallisce, la togliamo dalle chiamate in volo
                        // così al prossimo tentativo riproverà a fare il fetch
                        delete inFlightRequestsRef.current[id];
                        throw error;
                    }
                });

                // Aspettiamo tutte le promise (sia quelle nuove che quelle a cui ci siamo "agganciati")
                const newResults = await Promise.all(trackPromises);

                // 3. Salviamo i dati finali nella cache e puliamo la lista delle chiamate in volo
                newResults.forEach(({ eventId, tracks }) => {
                    trackCacheRef.current[eventId] = tracks;
                    delete inFlightRequestsRef.current[eventId];
                });
            }

            // 4. ORA RICOSTRUIAMO I DATI (Unione e Dizionario) basandoci sugli eventIds ATTUALI
            const currentAvailableTracksPerEvent: Record<string, string[]> = {};
            const unionMap = new Map<string, FilterOption>();
            let hasMissingTracks = false;

            eventIds.forEach(id => {
                // Peschiamo i dati dalla cache in modo sicurissimo ed efficiente (O(1))
                const tracksForEvent = trackCacheRef.current[id] || [];

                // Popoliamo il dizionario globale
                currentAvailableTracksPerEvent[id] = tracksForEvent.map(t => t.id);

                // Costruiamo l'unione PERFETTA senza duplicati
                tracksForEvent.forEach(track => {
                    if (!unionMap.has(track.id)) {
                        unionMap.set(track.id, track);
                    }
                });
            });

            if (audioTrack && !unionMap.has(audioTrack.id)) {
                setAudioTrack(null);
            }

            const unionTracks = Array.from(unionMap.values());

            // 5. Controlliamo se qualche evento tra quelli selezionati ha meno tracce dell'unione
            eventIds.forEach(id => {
                const tracksForEvent = trackCacheRef.current[id] || [];
                if (tracksForEvent.length < unionTracks.length) {
                    hasMissingTracks = true;
                }
            });

            // 6. Aggiorniamo gli stati (NON usiamo prev, abbiamo appena ricalcolato la vera unione pulita)
            setAudioTrackOptions(unionTracks);

            const callbackInfo = { availableTracksPerEvent: currentAvailableTracksPerEvent };

            if (unionTracks.length === 0) {
                onAudioTrackOptionLoad("error", callbackInfo);
            } else if (hasMissingTracks) {
                onAudioTrackOptionLoad("warning", callbackInfo);
            } else {
                onAudioTrackOptionLoad("success", callbackInfo);
            }

            // Logica di auto-selezione
            if (!audioTrack && audioTrackAutoSelection) {
                if (unionTracks.length === 1) {
                    setAudioTrack(unionTracks[0]);
                } else if (unionTracks.length > 1) {
                    setAudioTrack(DEFAULT_AUDIO_TRACK);
                }
            }

            onAudioTrackLoading(false);

        } catch (e) {
            enqueueSnackbar("Failed to load available audio tracks for the selected events");
            onAudioTrackLoading(false);
        }
    }
    const handleFadeDurationChange = (event: any, newValue: number) => {
        setFadeDuration(newValue);
    };

    useEffect(() => {
        if (eventIds && eventIds.length > 0) {
            loadAutoTracks()
        } else {
            setAudioTrackOptions([]);
        }
    }, [eventIds]);

    return (
        <Grid container spacing={1} direction="column" sx={{pl:1}} data-tour={"content_aggregation_materialization_settings_grid_complete"}>

            {/* 1. Aspect Ratio */}
            <Grid size={{ xs: 12 }}>
                <Box sx={{ borderRadius: '0px !important' }}>
                    <FormControl size="small" fullWidth data-tour={"content_aggregation_materialization_settings_aspect_ratio"}>
                        <Typography>Aspect Ratio</Typography>
                        <Autocomplete
                            id="aspect-ratio-autocomplete"
                            size="small"
                            disableClearable
                            // 1. Passiamo l'intero array di oggetti
                            options={ASPECT_RATIO_OPTIONS}
                            // Diciamo ad Autocomplete quale testo usare per la ricerca e l'input
                            getOptionLabel={(option) => option.label}
                            // 2. Mappiamo il valore stringa (aspectRatio) all'oggetto corrispondente
                            value={ASPECT_RATIO_OPTIONS.find(opt => opt.value === aspectRatio) || undefined}
                            // 3. Quando cambia, salviamo solo il 'value' nello stato
                            onChange={(event, newValue) => {
                                resetEditorialAssetSelection();
                                setAspectRatio(newValue ? newValue.value : '')}
                            }
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    paddingLeft: 1 // Aggiustiamo il padding per fare spazio all'icona
                                },
                                // Applichiamo lo stile al testo dell'input per renderlo simile al bottone
                                '& .MuiInputBase-input': {
                                    fontSize: '0.75rem',
                                    fontWeight: 200, // font-extralight
                                    fontFamily: 'inherit'
                                }
                            }}
                            // 4. Personalizziamo l'aspetto della tendina (Dropdown)
                            renderOption={(props, option, { selected }) => {
                                // Estraiamo la key da props per evitare warning di React
                                const { key, ...optionProps } = props;
                                return (
                                    <li
                                        data-tour={`materialization_settings_aspect_ratio_${option.description}`}
                                        key={key}
                                        {...optionProps}
                                        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-accent hover:text-accent-foreground"
                                    >
                                        <option.icon className={`h-4 w-4 ${option.color}`} />
                                        <div className="flex flex-col">
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono text-xs font-extralight">
                                                    {option.label}
                                                </span>
                                                {/* Mostriamo il Check se l'opzione è quella selezionata */}
                                                {selected && (
                                                    <Check className="h-3 w-3 text-primary" />
                                                )}
                                            </div>
                                            <span className="text-xs text-muted-foreground font-extralight">
                                                {option.description}
                                            </span>
                                        </div>
                                    </li>
                                );
                            }}
                            // 5. Personalizziamo il campo di input per aggiungere l'icona
                            renderInput={(params) => {
                                // Troviamo l'opzione selezionata per prenderne l'icona
                                const selectedOption = ASPECT_RATIO_OPTIONS.find(opt => opt.value === aspectRatio);

                                return (
                                    <TextField
                                        data-tour={"content_aggregation_materialization_settings_clickable_aspect_ratio"}
                                        {...params}
                                        placeholder="Select aspect ratio"
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: selectedOption ? (
                                                <InputAdornment position="start" sx={{ ml: 1, mr: 0 }}>
                                                    <selectedOption.icon className={`h-3.5 w-3.5 ${selectedOption.color}`} />
                                                </InputAdornment>
                                            ) : null,
                                        }}
                                    />
                                );
                            }}
                        />
                    </FormControl>
                </Box>
            </Grid>

            {/* 2. Audio Track (Condizionale) */}
            {showAudioTrackSelection && (usageMode !== AGGREGATION_USAGE_TYPE.PRESET) &&
                <Grid size={{ xs: 12 }}>
                    <Box sx={{ borderRadius: '0px !important' }}>
                        <FormControl size="small" fullWidth data-tour={"content_aggregation_materialization_settings_audio_track"}>
                            <Typography>Audio Track</Typography>
                            <CustomAutoComplete
                                label={""}
                                options={audioTrackOptions.map(option => ({
                                    ...option,
                                    value: intl.formatMessage({ id: option.value, defaultMessage: option.value })
                                }))}
                                value={audioTrack ? {
                                    ...audioTrack,
                                    value: intl.formatMessage({ id: audioTrack.value, defaultMessage: audioTrack.value })
                                } : null}
                                onChange={(_: any, newValue: any) => setAudioTrack(newValue)}
                                customAutocomplete={false}
                                multiple={false}
                                labelField={'value'}
                                valueField={'id'}
                                disabled={!eventIds || eventIds.length === 0}
                                disableClearable={true}
                            />
                        </FormControl>
                    </Box>
                </Grid>
            }

            {/* 3. Audio Fading */}
            <Grid size={{ xs: 12 }}>
                <Box sx={{ borderRadius: '0px !important' }} data-tour={"content_aggregation_materialization_settings_audio_fading_box"}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={fade}
                                onChange={(evt) => setFade(evt.target.checked)}
                                color="primary"
                            />
                        }
                        label="Audio Fading"
                    />

                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", opacity: fade ? 1 : 0.5, pointerEvents: fade ? 'auto' : 'none' }}>
                            <Typography sx={{ width: 120 }}>Duration</Typography>
                            <Slider
                                value={fadeDuration}
                                onChange={handleFadeDurationChange}
                                step={FADE_STEP}
                                min={FADE_MIN}
                                max={FADE_MAX}
                                disabled={!fade}
                                sx={{ flexGrow: 1, mr: 2 }}
                            />
                            <Typography sx={{ width: 50, textAlign: 'right' }}>{fadeDuration.toFixed(2)}s</Typography>
                        </Box>
                    </Box>
                </Box>
            </Grid>

            {/* 4. L-Cut */}
            <Grid size={{ xs: 12 }}>
                <Box sx={{ display: "flex", alignItems: "center", borderRadius: '0px !important' }} data-tour={"content_aggregation_materialization_settings_l_cut"}>
                    <Button
                        onClick={() => setLCut(!lCut)}
                        disabled={disableLCut}
                        size={'small'}
                        sx={{
                            minWidth: '90px',
                            height: '32.25px',
                            px: '2px !important',
                            fontSize: '12px !important',

                            // Colori di base
                            color: lCut ? '#ffffff' : 'rgba(255, 255, 255, 0.87)',
                            border: '0.5px solid rgba(255, 255, 255, 0.23)',
                            backgroundColor: lCut ? 'primary.main' : 'transparent',
                            borderColor: lCut ? 'primary.main' : 'rgba(255, 255, 255, 0.23)',

                            // Effetto Hover (solo se non disabilitato)
                            '&:not(.Mui-disabled):hover': {
                                backgroundColor: lCut ? 'primary.dark' : 'transparent',
                                borderColor: 'primary.main',
                                color: '#ffffff',
                            },

                            '&.Mui-disabled': {
                                color: 'rgba(255, 255, 255, 0.3)',
                                borderColor: 'rgba(255, 255, 255, 0.12)',
                                backgroundColor: lCut ? 'action.disabledBackground' : 'transparent',
                            }
                        }}
                    >
                        L-Cut
                    </Button>
                </Box>
            </Grid>
        </Grid>
    )
}

export default MaterializationSettings;